/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { BarSeriesOption, EChartsOption } from "echarts";
import { CallbackDataParams } from "echarts/types/dist/shared";
import { Bar } from "../../models/http/charts/bar-chart/bar.model";
import { ChartPattern, ChartPatternEnum } from "../../models/ui/charts.model";
import { COLORS } from "../constants";
import { overrideObject } from "../utils";
import { DEFAULT_CHART_OPTIONS } from "./chart-defaults";

export const CHART_PATTERNS: Record<ChartPatternEnum, ChartPattern> = {
	DIAGONAL_LINES: {
		color: COLORS.PATTERN,
		dashArrayX: [1, 0],
		dashArrayY: [1, 5],
		symbolSize: 1,
		rotation: -Math.PI / 4
	},
	CIRCLES: {
		color: COLORS.PATTERN,
		symbol: "circle",
		dashArrayX: [
			[10, 10],
			[0, 10, 10, 0]
		],
		dashArrayY: [6, 5],
		symbolSize: 1
	},
	SQUARES: {
		color: COLORS.PATTERN,
		dashArrayX: [3.5, 3.5],
		dashArrayY: [3.5, 3.5],
		symbolSize: 1,
		rotation: 0
	},
	TRIANGLES: {
		color: COLORS.PATTERN,
		symbol: "triangle",
		dashArrayX: [
			[10, 10],
			[0, 10, 10, 0]
		],
		dashArrayY: [7, 2],
		symbolSize: 0.75,
		rotation: Math.PI / 2
	}
};

export const FORECAST_MIXED_CHART_COLORS_BY_TYPE: string[] = [
	COLORS.TURQUOISE_100,
	COLORS.TURQUOISE_50,
	COLORS.YELLOW_100,
	COLORS.ORANGE_50,
	COLORS.GREEN_50
];

export const FORECAST_MIXED_CHART_COLORS_BY_SCOPE: string[] = [
	COLORS.TURQUOISE_100,
	COLORS.YELLOW_100,
	COLORS.ORANGE_50
];

export const FORECAST_MIXED_CHART_OVERRIDES_BY_TYPE: EChartsOption = {
	yAxis: [
		{
			name: "tCO2e Single bars"
		},
		{
			name: "tCO2e Trend line"
		}
	],
	series: [
		{},
		{},
		{},
		{},
		{
			itemStyle: {
				color: COLORS.SMART_NAVY,
				borderColor: COLORS.GREEN
			},
			lineStyle: {
				color: COLORS.GREEN
			},
			showSymbol: true,
			symbol: "rect",
			symbolSize: 8
		}
	]
};

export const FORECAST_MIXED_CHART_OVERRIDES_BY_SCOPE: EChartsOption = {
	yAxis: [
		{
			name: "tCO2e Single bars"
		},
		{
			name: "tCO2e Trend line"
		}
	],
	series: [
		{},
		{},
		{},
		{
			clip: true,
			itemStyle: {
				color: COLORS.SMART_NAVY,
				borderColor: COLORS.GREEN
			},
			lineStyle: {
				color: COLORS.GREEN
			},
			showSymbol: true,
			symbol: "rect",
			symbolSize: 8
		}
	]
};

export const FORECAST_MIXED_CHART_PATTERNS_BY_TYPE = [
	null,
	CHART_PATTERNS.CIRCLES,
	CHART_PATTERNS.DIAGONAL_LINES,
	CHART_PATTERNS.TRIANGLES,
	null
];

export const FORECAST_MIXED_CHART_PATTERNS_BY_SCOPE = [
	null,
	CHART_PATTERNS.DIAGONAL_LINES,
	CHART_PATTERNS.TRIANGLES
];

export const EMISSIONS_SHARE_COLORS_BY_TYPE: string[] = [
	COLORS.TURQUOISE_100,
	COLORS.TURQUOISE_50,
	COLORS.YELLOW_100,
	COLORS.ORANGE_50
];

export const EMISSIONS_SHARE_COLORS_BY_SCOPE: string[] = [
	COLORS.TURQUOISE_100,
	COLORS.YELLOW_100,
	COLORS.ORANGE_50
];

export const DETAIL_CHARTS_BY_TYPE_COLORS: string[][] = [
	[COLORS.GREY_50, COLORS.GREY_100, COLORS.TURQUOISE_100],
	[COLORS.GREY_50, COLORS.GREY_100, COLORS.TURQUOISE_50],
	[COLORS.GREY_50, COLORS.GREY_100, COLORS.YELLOW_100],
	[COLORS.GREY_50, COLORS.GREY_100, COLORS.ORANGE_50]
];

export const DETAIL_CHARTS_BY_SCOPE_COLORS: string[][] = [
	[COLORS.TURQUOISE_100, COLORS.TURQUOISE_50, COLORS.YELLOW_100, COLORS.ORANGE_50]
];

export const DETAIL_CHARTS_BY_TYPE_OVERRIDES: EChartsOption[] = [
	{
		series: {
			stack: "bar"
		}
	},
	{
		series: {
			stack: "bar"
		}
	},
	{
		series: {
			stack: "bar"
		}
	},
	{
		series: {
			stack: "bar"
		}
	}
];

export const DETAIL_CHARTS_BY_SCOPE_OVERRIDES: EChartsOption[] = [
	{
		series: {
			stack: "bar"
		}
	},
	{
		series: {
			stack: "bar"
		}
	},
	{
		series: {
			stack: "bar"
		}
	}
];

export const AVERAGE_CARBON_INTENSITY_BAR_CHART_OVERRIDES: EChartsOption = {
	series: {
		stack: "bar"
	}
};

export const SCENARIO_DIFFERENCES_BAR_CHART_OVERRIDES: EChartsOption = {
	series: [
		overrideObject(DEFAULT_CHART_OPTIONS["barX"]().series as BarSeriesOption, {
			stack: "bar",
			label: {
				show: true,
				formatter: function (params: CallbackDataParams) {
					const value = (params.data as Bar).value;
					return value ? `${value}` : "";
				}
			}
		}),
		overrideObject(DEFAULT_CHART_OPTIONS["barX"]().series as BarSeriesOption, {
			...(DEFAULT_CHART_OPTIONS["barX"]().series as BarSeriesOption),
			stack: "bar",
			label: {
				show: true,
				formatter: function (params: CallbackDataParams) {
					const percentage = (params.data as Bar).percentageDiff;

					return percentage
						? Number(percentage) > 0
							? `+${percentage}%`
							: `${percentage}%`
						: "";
				}
			}
		})
	]
};

export const CARBON_FOOTPRINT_BAR_CHART_OVERRIDES: EChartsOption = {
	series: {
		label: {
			show: true
		}
	},
	xAxis: {
		axisLabel: {
			width: 100,
			interval: 0
		}
	},
	dataZoom: [
		{
			type: "inside",
			minValueSpan: 5,
			maxValueSpan: 5
		}
	]
};

export const EMISSIONS_ANALYSIS_BAR_CHART_OVERRIDES: EChartsOption = {
	series: {
		label: {
			show: true
		}
	},
	xAxis: {
		axisLabel: {
			width: 100,
			interval: 0
		}
	},
	dataZoom: [
		{
			type: "inside",
			minValueSpan: 5,
			maxValueSpan: 5
		}
	]
};
